<template>
  <div
    class="sam-tabs"
    :class="{ 'is-simple': simple }"
    data-samora
  >
    <div class="sam-tabs__header">
      <ul
        ref="tabList"
        class="sam-tabs__list"
        role="tablist"
      >
        <template v-if="loading">
          <li
            v-for="i in 2"
            :key="i"
            class="sam-tabs__nav-item"
          >
            <span class="is-loading--bar" />
          </li>
        </template>
        <template v-else>
          <li
            v-for="(tab, index) in normalisedTabs"
            :key="index"
            :tabindex="selectedTabIndex === index ? 0 : -1"
            :aria-selected="selectedTabIndex === index"
            :aria-controls="tab.id"
            :data-tab="tab.id"
            :class="[
              {
                'is-disabled': disabled || tab.disabled,
                'is-selected': selectedTabIndex === index,
              },
            ]"
            class="sam-tabs__nav-item"
            role="tab"
            :title="tab.label"
            @click="setTab(index)"
            @keydown.right="focusNextTab()"
            @keydown.left="focusPreviousTab()"
            @keydown.esc="blur()"
          >
            <span class="sam-tabs__nav-item-label">{{ tab.label }}</span>
          </li>
        </template>
        <li class="sam-tabs__nav-item--empty">
          <slot name="empty"></slot>
        </li>
      </ul>
    </div>

    <div class="sam-tabs__content">
      <!-- @slot Use slot to show content for the selected tab -->
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * Tab object definition
 * @typedef {object} Tab
 * @property {string} label
 * @property {boolean} [disabled]
 */

const EVENTS = {
  TAB_CHANGED: 'tab-changed',
};

export default {
  name: 'SamTabs',
  props: {
    /**
     * If tabs is an array of strings OR the 'id' property is missing from a Tab object in the array,
     * Tab.id defaults to 'tab-${index}'
     */
    tabs: {
      /** @type {PropType<String[]> | PropType<Tab[]>} */
      type: Array,
      required: true,
    },
    /** If the tabs should be loading
     * @values true, false
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** If the tabs should be disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The initial tab index to be selected
     * @default 0
     */
    selectedTab: {
      type: Number,
      required: false,
      default: 0,
    },
    /** If the tabs should be shown in a minimalist way
     * @values true, false
     */
    simple: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      selectedTabIndex: 0,
      normalisedTabs: [],
    };
  },
  watch: {
    tabs: {
      handler() {
        // redo tabs normalisation
        this.normalisedTabs = [];

        let tab;
        let defaultTab;

        for (let i = 0; i < this.tabs.length; i += 1) {
          tab = this.tabs[i];
          defaultTab = { id: `tab-${i}` };

          if (typeof tab === 'string') {
            defaultTab.label = tab;
            this.normalisedTabs.push(defaultTab);
          } else {
            defaultTab.label = tab.label;

            this.normalisedTabs.push({ ...defaultTab, ...tab });
          }
        }
      },
      immediate: true,
    },
  },
  beforeMount() {
    this.selectedTabIndex = this.selectedTab;
  },
  mounted() {
    if (this.tabs.length) {
      this.focusSelectedTab();
    }
  },
  methods: {
    /**
     * Select a tab
     * @param {string|number} id
     * @public
     */
    setTab(id) {
      const tabIndexToChange = this.getTabIndex(id);

      // if the same tab is re-selected or disabled tab is selected, ignore
      if (
        tabIndexToChange === -1 ||
        this.selectedTabIndex === tabIndexToChange ||
        this.tabs[tabIndexToChange].disabled
      ) {
        return;
      }

      this.selectedTabIndex = tabIndexToChange;
      this.focusSelectedTab();

      /**
       * Tab changed event
       * @event tab-changed
       * @property {Tab} tab
       */
      this.$emit(EVENTS.TAB_CHANGED, this.tabs[tabIndexToChange]);
    },
    /**
     * Focus on next active tab
     */
    focusNextTab() {
      const numTabs = this.normalisedTabs.length;

      if (!numTabs) return;

      for (let nextTab = this.selectedTabIndex + 1; nextTab <= numTabs; nextTab += 1) {
        if (nextTab === numTabs) {
          nextTab = 0;
        }

        if (!this.normalisedTabs[nextTab].disabled) {
          this.setTab(nextTab);
          return;
        }
      }
    },
    /**
     * Focus on previous active tab
     */
    focusPreviousTab() {
      const numTabs = this.normalisedTabs.length;

      if (!numTabs) return;

      for (let prevTab = this.selectedTabIndex - 1; prevTab >= -1; prevTab -= 1) {
        if (prevTab < 0) {
          prevTab = numTabs - 1;
        }

        if (!this.normalisedTabs[prevTab].disabled) {
          this.setTab(prevTab);
          return;
        }
      }
    },
    /**
     * Remove keyboard focus from the tabs navigation
     */
    blur() {
      document.activeElement.blur();
    },
    focusSelectedTab() {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.tabList?.children?.[this.selectedTabIndex]?.focus?.();
    },
    /**
     * Returns the position of a visible tab
     * @param {string|number} id
     * @return {number}
     */
    getTabIndex(id) {
      if (typeof id === 'number') {
        return id;
      }

      return this.normalisedTabs.findIndex((tab) => tab.id === id);
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-tabs {
  @extend .d-flex, .flex-column;

  &__header {
    @extend .d-flex;
    width: 100%;
  }

  &__list {
    @extend .d-flex;
    width: 100%;
    overflow: auto;
  }

  &__nav-item {
    @extend .d-flex, .text--body-1, .items-center, .py-4, .px-8;
    height: 3em;
    border-bottom: $size--0-half solid $neutral--40;
    position: relative;
    min-width: fit-content;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    color: $pure--black;

    &:hover {
      background-color: $neutral--10;
    }

    &.is-disabled {
      color: $neutral--60;
      pointer-events: none;
      cursor: not-allowed;
    }

    &.is-selected {
      @include mix-weight--demi;
      background-color: $background--linen;
      border-bottom-color: $secondary--burning-orange;
    }

    &:focus-visible {
      outline: none;
      z-index: 1;
    }
  }

  &__nav-item--empty {
    width: 100%;
    background-color: transparent;
    border-bottom: $size--0-half solid $neutral--40;
  }

  &.is-simple {
    .sam-tabs__list {
      @extend .gap-6;
    }
    .sam-tabs__nav-item {
      height: auto;
      @extend .p-0;
      border: none;
      flex-flow: column;
      justify-content: center;

      &:hover,
      &.is-selected {
        background-color: transparent;
        @include mix-weight--demi;
      }

      &::before {
        // hack to prevent font-weight growth on hover to change nav width
        display: block;
        content: attr(title);
        @include mix-weight--demi;
        height: 0;
        visibility: hidden;
      }

      &:after {
        transition: all 0.1s;
        content: '';
        @extend .mt-1;
        display: block;
        white-space: wrap;
        width: 0;
        border-bottom: $size--0-half solid $secondary--burning-orange;
      }

      &.is-selected:after {
        width: $size--8;
      }
    }

    .sam-tabs__nav-item--empty {
      @extend .py-1;
      border: none;
    }
  }
}

/* Loading classes */
.is-loading--bar {
  @include skeleton-blink;
  width: 40px;
  height: 16px;
  border-radius: 8px;
}
</style>
